import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import { profileApi } from '../services/api';
import { formatQuantity } from '../utils/ingredientScaler';
import {
  X, ChevronLeft, ChevronRight, Play, Pause,
  RotateCcw, CheckCircle, List, ChefHat,
} from 'lucide-react';
import { Button, EmptyState } from '../components/common';

function TimerDisplay({ initialMinutes, onComplete }) {
  const [seconds, setSeconds] = useState(initialMinutes * 60);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (running && seconds > 0) {
      intervalRef.current = setInterval(() => {
        setSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            setRunning(false);
            onComplete?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running, seconds, onComplete]);

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const progress = 1 - seconds / (initialMinutes * 60);

  const reset = () => {
    setRunning(false);
    setSeconds(initialMinutes * 60);
    clearInterval(intervalRef.current);
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative w-40 h-40">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r="45" className="fill-none stroke-gray-200 dark:stroke-night-card" strokeWidth="6" />
          <circle
            cx="50" cy="50" r="45"
            className="fill-none stroke-primary-500 transition-[stroke-dasharray] duration-1000"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={`${progress * 283} 283`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-3xl font-mono font-semibold text-gray-900 dark:text-night-text" aria-live="polite">
            {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={reset}
          aria-label="Reset timer"
          className="w-10 h-10 rounded-full bg-gray-100 dark:bg-night-card text-gray-700 dark:text-night-text hover:bg-gray-200 flex items-center justify-center"
        >
          <RotateCcw size={18} />
        </button>
        <button
          onClick={() => setRunning(!running)}
          aria-label={running ? 'Pause timer' : 'Start timer'}
          className="w-12 h-12 rounded-full bg-primary-600 text-white hover:bg-primary-700 flex items-center justify-center"
        >
          {running ? <Pause size={20} /> : <Play size={20} />}
        </button>
      </div>
    </div>
  );
}

export default function CookingModePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { recipes, markAsCooked } = useApp();
  const { refreshSubscription } = useSubscription();

  const recipe = recipes.find((r) => r.id === parseInt(id, 10));
  const [currentStep, setCurrentStep] = useState(0);
  const [showIngredients, setShowIngredients] = useState(false);
  const [completed, setCompleted] = useState(false);
  const wakeLockRef = useRef(null);

  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) wakeLockRef.current = await navigator.wakeLock.request('screen');
      } catch {}
    };
    requestWakeLock();
    const handleVis = async () => {
      if (document.visibilityState === 'visible' && 'wakeLock' in navigator) {
        try { wakeLockRef.current = await navigator.wakeLock.request('screen'); } catch {}
      }
    };
    document.addEventListener('visibilitychange', handleVis);
    return () => {
      if (wakeLockRef.current) wakeLockRef.current.release();
      document.removeEventListener('visibilitychange', handleVis);
    };
  }, []);

  const steps = recipe?.steps || [];
  const totalSteps = steps.length;

  const goNext = useCallback(() => {
    if (currentStep < totalSteps - 1) setCurrentStep((prev) => prev + 1);
    else setCompleted(true);
  }, [currentStep, totalSteps]);

  const goPrev = useCallback(() => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  }, [currentStep]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goNext(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'Escape') navigate(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, navigate]);

  if (!recipe) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-night-bg flex items-center justify-center px-4">
        <EmptyState
          title="Recipe not found"
          description="We couldn't find that recipe."
          action={<Button onClick={() => navigate('/browse')}>Back to Recipes</Button>}
        />
      </div>
    );
  }

  const step = steps[currentStep];
  const progress = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  const handleComplete = async () => {
    await markAsCooked(recipe.id);
    try {
      await profileApi.update({ incrementCookingSession: true });
      refreshSubscription();
    } catch {}
    navigate(`/recipes/${recipe.id}`);
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-night-bg flex items-center justify-center px-4 safe-area-top safe-area-bottom">
        <div className="text-center max-w-sm">
          <div className="inline-flex w-20 h-20 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 mb-4" aria-hidden="true">
            <CheckCircle size={48} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-night-text">Nicely done!</h2>
          <p className="text-sm text-gray-500 dark:text-night-muted mt-1">You finished cooking {recipe.title}</p>
          <div className="flex flex-col gap-2 mt-6">
            <Button onClick={handleComplete} className="w-full">
              <ChefHat size={20} aria-hidden="true" />
              Mark as Cooked
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/recipes/${recipe.id}`)} className="w-full">
              Back to Recipe
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40 bg-white dark:bg-night-bg flex flex-col safe-area-top safe-area-bottom">
      <header className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-night-card">
        <button
          onClick={() => navigate(`/recipes/${recipe.id}`)}
          aria-label="Close cooking mode"
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-700 dark:text-night-text"
        >
          <X size={24} />
        </button>
        <h1 className="flex-1 text-base font-semibold text-gray-900 dark:text-night-text truncate text-center">
          {recipe.title}
        </h1>
        <button
          onClick={() => setShowIngredients(!showIngredients)}
          aria-pressed={showIngredients}
          aria-label="Toggle ingredients panel"
          className={[
            'p-2 rounded-lg',
            showIngredients
              ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
              : 'hover:bg-gray-100 dark:hover:bg-night-card text-gray-700 dark:text-night-text',
          ].join(' ')}
        >
          <List size={22} />
        </button>
      </header>

      <div className="px-4 py-2 border-b border-gray-100 dark:border-night-card">
        <div className="h-1.5 rounded-full bg-gray-200 dark:bg-night-card overflow-hidden">
          <div className="h-full bg-primary-500 transition-all" style={{ width: `${progress}%` }} />
        </div>
        <p className="text-xs text-gray-500 dark:text-night-muted text-center mt-1">
          Step {currentStep + 1} of {totalSteps}
        </p>
      </div>

      {showIngredients && (
        <div className="border-b border-gray-100 dark:border-night-card bg-gray-50 dark:bg-night-surface px-4 py-3 max-h-[40vh] overflow-y-auto">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-night-text mb-2">Ingredients</h3>
          <ul className="space-y-1 text-sm text-gray-700 dark:text-night-text">
            {(recipe.ingredients || []).map((ing, i) => (
              <li key={i}>
                <strong>{formatQuantity(ing.qty)} {ing.unit}</strong> {ing.item}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="relative flex-1 overflow-y-auto">
        <div className="absolute left-0 top-0 bottom-0 w-1/3 z-10" onClick={goPrev} aria-hidden="true" />
        <div className="absolute right-0 top-0 bottom-0 w-1/3 z-10" onClick={goNext} aria-hidden="true" />

        <div className="relative max-w-2xl mx-auto px-6 py-8 text-center space-y-6">
          <div className="inline-flex px-3 py-1 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-xs font-semibold uppercase tracking-wide">
            Step {step?.order || currentStep + 1}
          </div>
          <p className="text-2xl text-gray-900 dark:text-night-text leading-relaxed">{step?.instruction}</p>
          {step?.timerMinutes && (
            <div className="flex justify-center pt-2">
              <TimerDisplay key={`${currentStep}-${step.timerMinutes}`} initialMinutes={step.timerMinutes} />
            </div>
          )}
        </div>
      </div>

      <nav className="flex items-center justify-between gap-3 px-3 py-3 border-t border-gray-100 dark:border-night-card">
        <button
          onClick={goPrev}
          disabled={currentStep === 0}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-gray-700 dark:text-night-text hover:bg-gray-100 dark:hover:bg-night-card disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={24} />
          <span className="text-sm font-medium">Prev</span>
        </button>

        <div className="flex items-center gap-1.5">
          {steps.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentStep(i)}
              aria-label={`Go to step ${i + 1}`}
              className={[
                'w-2 h-2 rounded-full transition-colors',
                i === currentStep ? 'bg-primary-600 w-6' :
                i < currentStep ? 'bg-primary-300' :
                'bg-gray-300 dark:bg-night-card',
              ].join(' ')}
            />
          ))}
        </div>

        <button
          onClick={goNext}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 font-medium"
        >
          <span className="text-sm">{currentStep === totalSteps - 1 ? 'Done' : 'Next'}</span>
          {currentStep === totalSteps - 1 ? <CheckCircle size={24} /> : <ChevronRight size={24} />}
        </button>
      </nav>
    </div>
  );
}
